import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { useColorScheme } from "./useColorScheme";
import { App } from "@capacitor/app";
import { EdgeToEdge } from "@capawesome/capacitor-android-edge-to-edge-support"; // Keep for insets/status

// Exported setup function
export async function setupStatusBar(currentIsDark: boolean) {
  if (Capacitor.getPlatform() === "web") return;

  const backgroundColor = currentIsDark ? "#14161C" : "#FFFFFF";

  try {
    const platform = Capacitor.getPlatform();

    if (platform === "android") {
      const style = currentIsDark ? Style.Dark : Style.Light;

      // Set status bar via edge-to-edge plugin
      await EdgeToEdge.setBackgroundColor({ color: backgroundColor });
      await StatusBar.setStyle({ style });

      // Retry for reliability
      setTimeout(async () => {
        await EdgeToEdge.setBackgroundColor({ color: backgroundColor });
        await StatusBar.setStyle({ style });
      }, 500);
    } else if (platform === "ios") {
      const style = currentIsDark ? Style.Dark : Style.Light;
      await StatusBar.setStyle({ style });

      // Retry for iOS
      setTimeout(async () => {
        await StatusBar.setStyle({ style });
      }, 300);
    }
  } catch (error) {
    // Handle silently
  }
}

function useNativeBarsFromTailwind() {
  const { currentIsDark } = useColorScheme();
  const [isPermissionDialogOpen, setIsPermissionDialogOpen] = useState(false);

  useEffect(() => {
    if (Capacitor.getPlatform() === "web") return;
    // Initial status bar setup
    setupStatusBar(currentIsDark);

    // App state change listener for dialogs/resume
    const appStateChangeListenerPromise = App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        setIsPermissionDialogOpen(false);
        setTimeout(() => setupStatusBar(currentIsDark), 100);
        setTimeout(() => setupStatusBar(currentIsDark), 500);
      } else {
        setIsPermissionDialogOpen(true);
      }
    });

    const handleResumeEvent = () => {
      if (isPermissionDialogOpen) {
        setIsPermissionDialogOpen(false);
        setupStatusBar(currentIsDark);
        setTimeout(() => setupStatusBar(currentIsDark), 200);
        setTimeout(() => setupStatusBar(currentIsDark), 500);
      }
    };

    document.addEventListener("resume", handleResumeEvent);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setTimeout(() => setupStatusBar(currentIsDark), 300);
      }
    };

    const handleBlur = () => {
      setIsPermissionDialogOpen(true);
    };

    const handleFocus = () => {
      if (isPermissionDialogOpen) {
        setIsPermissionDialogOpen(false);
        setupStatusBar(currentIsDark);
        setTimeout(() => setupStatusBar(currentIsDark), 100);
        setTimeout(() => setupStatusBar(currentIsDark), 300);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);

    return () => {
      appStateChangeListenerPromise.then((listener) => listener.remove());
      document.removeEventListener("resume", handleResumeEvent);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
    };
  }, [currentIsDark, isPermissionDialogOpen]);

  return null;
}

export default useNativeBarsFromTailwind;
